import { useEffect, useState } from "react";
import { ArrowDown, ArrowUp, Check, Headphones, Loader2, Pencil, Plus, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const inputCls =
  "w-full rounded-lg border border-white/10 bg-[#0B0B0E] px-3 py-2.5 text-sm text-white placeholder-zinc-600 outline-none focus:border-emerald-300/60";

const EMPTY = { url: "", title: "", color: "#181e14" };

function PlaylistForm({ initial, onSaved, onCancel, isNew }) {
  const [form, setForm] = useState(initial || EMPTY);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setForm(initial || EMPTY); }, [initial]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        url: form.url.trim(),
        title: form.title.trim(),
        color: (form.color || "#181e14").trim(),
      };
      let saved;
      if (isNew) {
        ({ data: saved } = await api.post("/admin/soundcloud", payload));
      } else {
        ({ data: saved } = await api.put(`/admin/soundcloud/${initial.id}`, payload));
      }
      toast.success(isNew ? "Playlist added" : "Playlist saved");
      onSaved(saved);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const prefix = isNew ? "admin-sc-new" : `admin-sc-edit-${initial?.id}`;

  return (
    <form
      onSubmit={submit}
      className="space-y-3 rounded-2xl border border-emerald-300/30 bg-[#141419] p-5"
      data-testid={`${prefix}-form`}
    >
      <input
        required
        value={form.url}
        onChange={(e) => setForm({ ...form, url: e.target.value })}
        placeholder="https://soundcloud.com/your-name/sets/playlist-name  —  or paste the full iframe"
        className={inputCls}
        data-testid={`${prefix}-url`}
      />
      <input
        value={form.title}
        onChange={(e) => setForm({ ...form, title: e.target.value })}
        placeholder="Title (shown under the player — e.g. Underground Takeover)"
        className={inputCls}
        data-testid={`${prefix}-title`}
      />
      <div className="flex items-center gap-3">
        <label className="flex-1">
          <span className="mb-1 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">Player color (hex)</span>
          <input
            value={form.color}
            onChange={(e) => setForm({ ...form, color: e.target.value })}
            placeholder="#181e14"
            className={inputCls}
            data-testid={`${prefix}-color`}
          />
        </label>
        <div
          className="mt-5 h-10 w-10 shrink-0 rounded-lg border border-white/15"
          style={{ background: /^#[0-9A-Fa-f]{6}$/.test(form.color || "") ? form.color : "#181e14" }}
        />
      </div>
      <div className="flex gap-2 pt-1">
        <button
          type="submit"
          disabled={busy}
          data-testid={`${prefix}-save`}
          className="flex items-center gap-2 rounded-full bg-emerald-300 px-5 py-2 text-[11px] font-bold uppercase tracking-wider text-black hover:bg-emerald-200 disabled:opacity-50"
        >
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
          {isNew ? "Add Playlist" : "Save"}
        </button>
        <button
          type="button"
          onClick={onCancel}
          disabled={busy}
          className="flex items-center gap-2 rounded-full border border-white/15 px-5 py-2 text-[11px] font-bold uppercase tracking-wider text-zinc-300 hover:border-emerald-300/40 hover:text-emerald-200"
        >
          <X size={12} /> Cancel
        </button>
      </div>
      <p className="text-[11px] text-zinc-600">
        You can paste the full SoundCloud iframe — we'll pull the share URL out automatically.
      </p>
    </form>
  );
}

export function SoundcloudTab() {
  const [items, setItems] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [addingNew, setAddingNew] = useState(false);
  const [busyOrder, setBusyOrder] = useState(false);

  const load = () => api.get("/admin/soundcloud").then((r) => setItems(r.data));
  useEffect(() => { load().catch(() => {}); }, []);

  const commitOrder = async (next) => {
    setItems(next);
    setBusyOrder(true);
    try {
      await api.patch("/admin/soundcloud/order", { ids: next.map((x) => x.id) });
    } catch {
      toast.error("Couldn't save order — refreshing");
      load();
    } finally {
      setBusyOrder(false);
    }
  };

  const move = (idx, delta) => {
    const target = idx + delta;
    if (target < 0 || target >= items.length) return;
    const next = [...items];
    [next[idx], next[target]] = [next[target], next[idx]];
    commitOrder(next);
  };

  const remove = async (sid) => {
    if (!window.confirm("Remove this playlist from the catalog page?")) return;
    await api.delete(`/admin/soundcloud/${sid}`);
    load();
  };

  const onSaved = () => {
    load();
    setEditingId(null);
    setAddingNew(false);
  };

  return (
    <div className="space-y-4" data-testid="admin-soundcloud-tab">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Headphones size={14} className="text-emerald-300" />
          <p className="font-heading font-bold text-white">SoundCloud playlists</p>
          {busyOrder && <Loader2 size={11} className="animate-spin text-emerald-300" />}
        </div>
        {!addingNew && (
          <button
            type="button"
            onClick={() => { setAddingNew(true); setEditingId(null); }}
            data-testid="admin-sc-add"
            className="flex items-center gap-2 rounded-full bg-emerald-300 px-4 py-2 text-[11px] font-bold uppercase tracking-wider text-black hover:bg-emerald-200"
          >
            <Plus size={12} /> Add Playlist
          </button>
        )}
      </div>

      {addingNew && <PlaylistForm isNew initial={EMPTY} onSaved={onSaved} onCancel={() => setAddingNew(false)} />}

      <div className="space-y-2">
        {items.map((p, idx) => {
          const isEditing = editingId === p.id;
          if (isEditing) {
            return (
              <div key={p.id} data-testid={`admin-sc-row-${p.id}`}>
                <PlaylistForm initial={p} onSaved={onSaved} onCancel={() => setEditingId(null)} />
              </div>
            );
          }
          return (
            <div
              key={p.id}
              className="flex items-center gap-3 rounded-2xl border border-white/10 bg-[#141419] p-4"
              data-testid={`admin-sc-row-${p.id}`}
            >
              <span className="font-mono text-[11px] uppercase text-zinc-500">{idx + 1}</span>
              <div
                className="h-8 w-8 shrink-0 rounded-lg border border-white/10"
                style={{ background: /^#[0-9A-Fa-f]{6}$/.test(p.color || "") ? p.color : "#181e14" }}
              />
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-white">{p.title || "SoundCloud playlist"}</p>
                <p className="truncate font-mono text-[11px] text-zinc-500">{p.url}</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => { setEditingId(p.id); setAddingNew(false); }}
                  data-testid={`admin-sc-edit-${p.id}`}
                  aria-label="Edit"
                  className="rounded-full border border-white/10 p-2 text-emerald-200 hover:border-emerald-300/40 hover:bg-emerald-300/10"
                >
                  <Pencil size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => move(idx, -1)}
                  disabled={idx === 0 || busyOrder}
                  data-testid={`admin-sc-up-${p.id}`}
                  aria-label="Move up"
                  className="rounded-full border border-white/10 p-2 text-zinc-300 hover:border-emerald-300/40 hover:bg-emerald-300/10 disabled:cursor-not-allowed disabled:opacity-30"
                >
                  <ArrowUp size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => move(idx, 1)}
                  disabled={idx === items.length - 1 || busyOrder}
                  data-testid={`admin-sc-down-${p.id}`}
                  aria-label="Move down"
                  className="rounded-full border border-white/10 p-2 text-zinc-300 hover:border-emerald-300/40 hover:bg-emerald-300/10 disabled:cursor-not-allowed disabled:opacity-30"
                >
                  <ArrowDown size={13} />
                </button>
                <button
                  type="button"
                  onClick={() => remove(p.id)}
                  data-testid={`admin-sc-delete-${p.id}`}
                  aria-label="Delete"
                  className="rounded-full border border-red-500/30 p-2 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 size={13} />
                </button>
              </div>
            </div>
          );
        })}
        {items.length === 0 && !addingNew && (
          <p className="rounded-2xl border border-dashed border-white/10 p-6 text-center text-sm text-zinc-600">
            No SoundCloud playlists yet — paste one above.
          </p>
        )}
      </div>
    </div>
  );
}
